/**
 * RadialLayoutEngine — pure, size-aware radial layout primitives.
 *
 * Ported from the magnetic-focus prototype (`twg-radial-layout.ts`) but
 * adapted for incremental graph exploration:
 *   - `layoutDiscovery` — initial discovery view: center node + ~12
 *     neighbours arranged around an ellipse, grouped by typename, with
 *     AABB collision resolution.
 *   - `placeAroundCenter` — incremental expansion: returns positions for
 *     ONLY the new neighbours; existing graph-space positions are
 *     preserved. New nodes are placed on a ring around the center; if a
 *     candidate slot would overlap an existing node beyond a threshold,
 *     the algorithm walks angular slots until it finds a free one.
 *
 * Both call paths use rectangular (AABB) collision resolution rather than
 * circular bounding-radius pushes, so wide-short cards (~200×60) don't get
 * shoved 100+ px in unnecessary directions.
 */

import { relaxAroundCenter } from './OverlapRemoval';

// ============================================================================
// Types
// ============================================================================

export interface NodeSize {
	width: number;
	height: number;
}

export interface NeighbourSpec {
	id: string;
	/** Used for grouping order via `NODE_LAYOUT_PRIORITY`. */
	typename: string;
	/** Used as a tie-breaker in sort. */
	title?: string | null;
}

export interface PlacedNode {
	id: string;
	x: number;
	y: number;
}

export interface DiscoveryInput {
	centerId: string;
	centerSize?: NodeSize;
	neighbours: NeighbourSpec[];
	/** id → size (with typename-keyed defaults applied by callers). */
	sizes: Map<string, NodeSize>;
	/** Optional override of the base ellipse semi-axes. */
	baseRx?: number;
	baseRy?: number;
}

export interface PlaceAroundCenterInput {
	centerPos: { x: number; y: number };
	centerSize?: NodeSize;
	newNeighbours: NeighbourSpec[];
	/** All positions currently in the graph (including the center). */
	existingPositions: Map<string, { x: number; y: number }>;
	/** id → size for every node referenced (existing + new). */
	sizes: Map<string, NodeSize>;
	/** 0..1; AABB area-overlap fraction that disqualifies a slot. */
	overlapThreshold?: number;
	/**
	 * Position of the ultimate layout root (typically the user node).
	 * When provided, new neighbours are fanned on the *outward* side of
	 * the clicked parent (the ring arc centres on `root → center`) so
	 * children appear on the far side, not back-toward the root.
	 */
	rootPos?: { x: number; y: number };
}

// ============================================================================
// Constants (start values; tunable via callers)
// ============================================================================

const DEFAULT_BASE_RX = 260;
const DEFAULT_BASE_RY = 220;
const MIN_GAP_PX = 28;
// Padding (in px) added to every node's half-extents during overlap
// detection. Increases visual breathing room between nodes so labels
// don't visually collide with neighbouring nodes and the graph reads
// as less cluttered. Bumped from (16, 12) → (24, 18) — modest 50% bump.
//
// Exported so the ambient-wobble system (`AmbientWobble.ts`) can size
// each node's drift as a fraction of this static buffer, guaranteeing
// neighbouring nodes can never wobble far enough to overlap.
export const COLLISION_PADDING_X = 24;
export const COLLISION_PADDING_Y = 18;
const COLLISION_ITERATIONS = 80;
const COLLISION_DAMPING = 0.55;
const DISCOVERY_COMPACT_SCALE_X = 0.9;
const DISCOVERY_COMPACT_SCALE_Y = 0.76;

// Fraction of the smaller (padded) AABB area that must be overlapping
// before a candidate slot is rejected. Lowered from 0.3 → 0.05 so we
// reject candidates with even a small visual overlap — the padded
// aabbOverlapFraction already inflates sizes by COLLISION_PADDING_X/Y,
// so a 5% overlap on the inflated box corresponds to genuine visual
// proximity, not just numerical noise.
const DEFAULT_OVERLAP_THRESHOLD = 0.05;
/** How many extra angular slots we try when the natural slot is blocked. */
const SLOT_SEARCH_STEPS = 24;
/** Fan arc for incremental expansion (radians). */
const EXPANSION_ARC_SPREAD = Math.PI * 1.4;
/**
 * When angular rotation can't find a clear slot for a new node at the
 * default ring radius, we push it outward along its natural ray in
 * `RADIAL_FALLBACK_STEP_PX` increments. At each enlarged radius we do
 * another small angular sweep before growing further. The growth is
 * capped at `RADIAL_FALLBACK_MAX_MULTIPLIER × ring.rx` so the node
 * can't be ejected unboundedly on a pathologically crowded canvas.
 */
const RADIAL_FALLBACK_STEP_PX = 80;
const RADIAL_FALLBACK_MAX_MULTIPLIER = 4;
/**
 * Number of angular candidates to try at each enlarged radius before
 * stepping the radius outward again. Smaller than `SLOT_SEARCH_STEPS`
 * because the further out we are, the less crowded space becomes.
 */
const RADIAL_FALLBACK_ANGULAR_STEPS = 8;

/**
 * Lower number = higher priority (placed earlier in the radial sweep).
 * Unknown typenames fall back to `99`. Both raw typenames (UpperCamel) and
 * prototype-style entity-type strings are mapped so we don't accidentally
 * regress to "everything else" ordering.
 */
const NODE_LAYOUT_PRIORITY: Record<string, number> = {
	BitbucketRepository: 0,
	'bitbucket:repository': 0,
	ExternalDocument: 1,
	ExternalVideo: 1,
	'3p:document': 1,
	ConfluencePage: 2,
	'confluence:page': 2,
	JiraIssue: 3,
	'jira:issue': 3,
	LoomVideo: 4,
	'loom:video': 4,
	AtlassianUser: 5,
	'identity:user': 5,
};

// ============================================================================
// Helpers
// ============================================================================

function ellipseCircumference(rx: number, ry: number): number {
	return Math.PI * (3 * (rx + ry) - Math.sqrt((3 * rx + ry) * (rx + 3 * ry)));
}

function priorityFor(typename: string): number {
	return NODE_LAYOUT_PRIORITY[typename] ?? 99;
}

function sortNeighbours<T extends NeighbourSpec>(neighbours: T[]): T[] {
	return [...neighbours].sort((a, b) => {
		const dp = priorityFor(a.typename) - priorityFor(b.typename);
		if (dp !== 0) return dp;
		return (a.title ?? a.id).localeCompare(b.title ?? b.id);
	});
}

function sizeOr(sizes: Map<string, NodeSize>, id: string, fallback: NodeSize): NodeSize {
	return sizes.get(id) ?? fallback;
}

const FALLBACK_SIZE: NodeSize = { width: 200, height: 60 };

function discoveryPadding(count: number): { x: number; y: number } {
	return {
		x: 16 + Math.min(10, Math.floor(count / 4) * 2),
		y: 12 + Math.min(8, Math.floor(count / 4) * 2),
	};
}

/**
 * Compute the smallest ellipse `(rx, ry)` ≥ `(baseRx, baseRy)` whose
 * circumference can hold the summed arc-widths of every neighbour plus
 * `minGap`. Keeps the original aspect ratio.
 */
function computeRingRadius(
	neighbours: NeighbourSpec[],
	sizes: Map<string, NodeSize>,
	baseRx: number,
	baseRy: number,
	minGap: number,
): { rx: number; ry: number } {
	if (neighbours.length === 0) return { rx: baseRx, ry: baseRy };

	const totalArc = neighbours.reduce((sum, n) => {
		const size = sizeOr(sizes, n.id, FALLBACK_SIZE);
		return sum + size.width + minGap;
	}, 0);

	const baseCircumference = ellipseCircumference(baseRx, baseRy);
	if (totalArc <= baseCircumference) {
		return { rx: baseRx, ry: baseRy };
	}

	const scale = totalArc / baseCircumference;
	return { rx: baseRx * scale, ry: baseRy * scale };
}

/**
 * Distribute neighbours along an ellipse at `(centerX, centerY)` with
 * arc-widths proportional to each node's measured width + `gap`. Starts
 * at the top of the ring (-π/2) and walks clockwise.
 */
function distributeOnEllipse(
	neighbours: NeighbourSpec[],
	sizes: Map<string, NodeSize>,
	rx: number,
	ry: number,
	centerX: number,
	centerY: number,
	gap: number,
): PlacedNode[] {
	if (neighbours.length === 0) return [];

	const entries = neighbours.map((n) => ({
		id: n.id,
		arcWidth: sizeOr(sizes, n.id, FALLBACK_SIZE).width + gap,
	}));
	const totalArc = entries.reduce((sum, e) => sum + e.arcWidth, 0);

	const result: PlacedNode[] = [];
	let cumulativeFraction = 0;

	for (const entry of entries) {
		const fraction = entry.arcWidth / totalArc;
		const angle = -Math.PI / 2 + (cumulativeFraction + fraction / 2) * Math.PI * 2;
		result.push({
			id: entry.id,
			x: centerX + Math.cos(angle) * rx,
			y: centerY + Math.sin(angle) * ry,
		});
		cumulativeFraction += fraction;
	}

	return result;
}

/**
 * AABB collision resolution. Mutates positions in place.
 *
 * For each overlapping pair, push along whichever axis has the smaller
 * overlap (so wide-short cards mostly slide horizontally, tall-narrow
 * ones mostly vertically). The center node (if present in `fixedIds`)
 * is never moved.
 */
function resolveCollisions(
	nodes: PlacedNode[],
	sizes: Map<string, NodeSize>,
	fixedIds: Set<string>,
	paddingX: number,
	paddingY: number,
	iterations: number,
): void {
	for (let iter = 0; iter < iterations; iter++) {
		let maxOverlap = 0;

		for (let i = 0; i < nodes.length; i++) {
			for (let j = i + 1; j < nodes.length; j++) {
				const a = nodes[i];
				const b = nodes[j];
				const sA = sizeOr(sizes, a.id, FALLBACK_SIZE);
				const sB = sizeOr(sizes, b.id, FALLBACK_SIZE);

				const hwA = sA.width / 2 + paddingX;
				const hhA = sA.height / 2 + paddingY;
				const hwB = sB.width / 2 + paddingX;
				const hhB = sB.height / 2 + paddingY;

				const overlapX = hwA + hwB - Math.abs(a.x - b.x);
				const overlapY = hhA + hhB - Math.abs(a.y - b.y);

				if (overlapX <= 0 || overlapY <= 0) continue;

				maxOverlap = Math.max(maxOverlap, Math.min(overlapX, overlapY));

				const aFixed = fixedIds.has(a.id);
				const bFixed = fixedIds.has(b.id);
				if (aFixed && bFixed) continue;

				if (overlapX < overlapY) {
					const sign = a.x >= b.x ? 1 : -1;
					const push = overlapX * COLLISION_DAMPING;
					if (!aFixed) a.x += sign * (bFixed ? push * 2 : push);
					if (!bFixed) b.x -= sign * (aFixed ? push * 2 : push);
				} else {
					const sign = a.y >= b.y ? 1 : -1;
					const push = overlapY * COLLISION_DAMPING;
					if (!aFixed) a.y += sign * (bFixed ? push * 2 : push);
					if (!bFixed) b.y -= sign * (aFixed ? push * 2 : push);
				}
			}
		}

		if (maxOverlap < 1) break;
	}
}

/**
 * Returns the AABB area-overlap fraction relative to the smaller box.
 * 0 = no overlap, 1 = the smaller box is fully inside the larger one.
 *
 * Both boxes are inflated by `paddingX/paddingY` (defaulting to the
 * collision padding constants) before the overlap test, so callers
 * automatically get a "breathing room" margin around every node — this
 * is what gives labels visible separation from neighbouring nodes.
 */
function aabbOverlapFraction(
	a: { x: number; y: number },
	sizeA: NodeSize,
	b: { x: number; y: number },
	sizeB: NodeSize,
	paddingX: number = COLLISION_PADDING_X,
	paddingY: number = COLLISION_PADDING_Y,
): number {
	const dx = Math.abs(a.x - b.x);
	const dy = Math.abs(a.y - b.y);
	const inflatedHalfWA = sizeA.width / 2 + paddingX;
	const inflatedHalfHA = sizeA.height / 2 + paddingY;
	const inflatedHalfWB = sizeB.width / 2 + paddingX;
	const inflatedHalfHB = sizeB.height / 2 + paddingY;
	const overlapW = inflatedHalfWA + inflatedHalfWB - dx;
	const overlapH = inflatedHalfHA + inflatedHalfHB - dy;
	if (overlapW <= 0 || overlapH <= 0) return 0;
	const overlapArea = overlapW * overlapH;
	// Use the inflated areas as the divisor too so the fraction stays
	// in the same range — otherwise a small node padded heavily would
	// always read as "fully inside" the larger inflated box.
	const inflatedAreaA = (sizeA.width + 2 * paddingX) * (sizeA.height + 2 * paddingY);
	const inflatedAreaB = (sizeB.width + 2 * paddingX) * (sizeB.height + 2 * paddingY);
	const smallerArea = Math.min(inflatedAreaA, inflatedAreaB);
	if (smallerArea <= 0) return 0;
	return Math.min(1, overlapArea / smallerArea);
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Initial discovery layout: the center node sits at (0, 0); neighbours
 * are arranged on an ellipse around it. Grouped by typename via
 * `NODE_LAYOUT_PRIORITY`. Returns a `Map<id, {x, y}>` containing the
 * center and every neighbour.
 */
export function layoutDiscovery(input: DiscoveryInput): Map<string, { x: number; y: number }> {
	const { centerId, neighbours, sizes, baseRx = DEFAULT_BASE_RX, baseRy = DEFAULT_BASE_RY } = input;

	const sorted = sortNeighbours(neighbours);
	const ring = computeRingRadius(sorted, sizes, baseRx, baseRy, MIN_GAP_PX);

	const placed: PlacedNode[] = [{ id: centerId, x: 0, y: 0 }];
	placed.push(...distributeOnEllipse(sorted, sizes, ring.rx, ring.ry, 0, 0, MIN_GAP_PX));

	const fixed = new Set<string>([centerId]);
	resolveCollisions(
		placed,
		sizes,
		fixed,
		COLLISION_PADDING_X,
		COLLISION_PADDING_Y,
		COLLISION_ITERATIONS,
	);

	if (neighbours.length > 0) {
		for (const node of placed) {
			if (node.id === centerId) continue;
			node.x *= DISCOVERY_COMPACT_SCALE_X;
			node.y *= DISCOVERY_COMPACT_SCALE_Y;
		}

		const padding = discoveryPadding(neighbours.length);
		resolveCollisions(placed, sizes, fixed, padding.x, padding.y, COLLISION_ITERATIONS);

		const relaxed = relaxAroundCenter({
			center: { x: 0, y: 0 },
			centerId,
			nodes: placed,
			sizes,
			padding,
			iterations: 4,
		});

		for (const node of placed) {
			const next = relaxed.get(node.id);
			if (!next) continue;
			node.x = next.x;
			node.y = next.y;
		}
	}

	const out = new Map<string, { x: number; y: number }>();
	for (const p of placed) out.set(p.id, { x: p.x, y: p.y });
	return out;
}

/**
 * Place a batch of new neighbours around an existing center node. The
 * positions of every node already on the canvas are preserved verbatim.
 *
 * Algorithm:
 *   1. Compute a size-aware ring `(rx, ry)` via `computeRingRadius` so
 *      the expansion spacing matches initial discovery for the same
 *      neighbour count + widths.
 *   2. Bias the fan *outward* from the layout root: the arc is centred
 *      on the angle `root → centerPos` and spans `EXPANSION_ARC_SPREAD`
 *      radians, so children appear on the far side of the clicked
 *      parent rather than wrapping back over the root.
 *   3. Distribute neighbours arc-width-proportionally across that arc
 *      (identical weighting to `distributeOnEllipse`). For each slot
 *      whose AABB would overlap an existing non-sibling, walk angular
 *      offsets (+/-) until a clear slot is found.
 *   4. Run a collision-resolution pass with existing nodes as fixed
 *      obstacles.
 *
 * Returns ONLY the new placements; the caller merges them into the
 * authoritative position store.
 */
export function placeAroundCenter(input: PlaceAroundCenterInput): PlacedNode[] {
	const {
		centerPos,
		newNeighbours,
		existingPositions,
		sizes,
		overlapThreshold = DEFAULT_OVERLAP_THRESHOLD,
		rootPos,
	} = input;

	if (newNeighbours.length === 0) return [];

	const sorted = sortNeighbours(newNeighbours);

	// Size-aware ring — identical to discovery so expansion and initial
	// layouts have the same gap/size ratio regardless of neighbour count.
	const ring = computeRingRadius(sorted, sizes, DEFAULT_BASE_RX, DEFAULT_BASE_RY, MIN_GAP_PX);

	// Fan direction: away from the root (user node). If the center *is*
	// the root (or no root provided), fall back to straight up.
	const outwardAngle =
		rootPos && (rootPos.x !== centerPos.x || rootPos.y !== centerPos.y)
			? Math.atan2(centerPos.y - rootPos.y, centerPos.x - rootPos.x)
			: -Math.PI / 2;

	// Build a fixed array of existing nodes (id + position + size) for
	// quick collision checks. Excludes ids we're about to place.
	const newIds = new Set(sorted.map((n) => n.id));
	const existing: Array<{ id: string; x: number; y: number; size: NodeSize }> = [];
	for (const [id, pos] of existingPositions) {
		if (newIds.has(id)) continue;
		existing.push({ id, x: pos.x, y: pos.y, size: sizeOr(sizes, id, FALLBACK_SIZE) });
	}

	// Arc-width-proportional natural slot angles across the outward arc.
	const entries = sorted.map((n) => ({
		arcWidth: sizeOr(sizes, n.id, FALLBACK_SIZE).width + MIN_GAP_PX,
	}));
	const totalArc = entries.reduce((sum, e) => sum + e.arcWidth, 0);
	const naturalAngles: number[] = [];
	let cumulativeFraction = 0;
	for (const entry of entries) {
		const fraction = entry.arcWidth / totalArc;
		const angle =
			outwardAngle -
			EXPANSION_ARC_SPREAD / 2 +
			(cumulativeFraction + fraction / 2) * EXPANSION_ARC_SPREAD;
		naturalAngles.push(angle);
		cumulativeFraction += fraction;
	}

	// Angular step used when the natural slot is blocked — rotate, don't
	// grow the ring (keeps the spacing consistent across retries).
	const angleStep = EXPANSION_ARC_SPREAD / Math.max(sorted.length, 1) / 3;

	const placed: PlacedNode[] = [];

	for (let i = 0; i < sorted.length; i++) {
		const neighbour = sorted[i];
		const size = sizeOr(sizes, neighbour.id, FALLBACK_SIZE);
		const naturalAngle = naturalAngles[i];

		let chosen: PlacedNode | null = null;

		for (let step = 0; step < SLOT_SEARCH_STEPS; step++) {
			const sign = step % 2 === 0 ? 1 : -1;
			const offset = Math.ceil(step / 2) * angleStep;
			const angle = naturalAngle + sign * offset;

			const candidate: PlacedNode = {
				id: neighbour.id,
				x: centerPos.x + Math.cos(angle) * ring.rx,
				y: centerPos.y + Math.sin(angle) * ring.ry,
			};

			let blocked = false;
			for (const ex of existing) {
				if (aabbOverlapFraction(candidate, size, ex, ex.size) > overlapThreshold) {
					blocked = true;
					break;
				}
			}
			if (blocked) continue;

			let collidesWithSibling = false;
			for (const sib of placed) {
				const sibSize = sizeOr(sizes, sib.id, FALLBACK_SIZE);
				if (aabbOverlapFraction(candidate, size, sib, sibSize) > overlapThreshold) {
					collidesWithSibling = true;
					break;
				}
			}
			if (collidesWithSibling) continue;

			chosen = candidate;
			break;
		}

		if (!chosen) {
			// Per-node radial-expansion fallback: angular rotation at
			// the default ring radius didn't find a clear slot. Push the
			// candidate OUTWARD along its natural ray in fixed steps —
			// existing nodes stay put, only this one node moves further
			// from the parent. At each enlarged radius do a small angular
			// sweep before growing further, so we don't push outward more
			// than necessary.
			//
			// This preserves the invariant that already-placed nodes
			// (existing siblings AND already-placed new siblings in this
			// batch) keep their positions; the conflicting node is the
			// only thing that adapts.
			const baseRx = ring.rx;
			const baseRy = ring.ry;
			const aspect = baseRy / baseRx;
			const maxRx = baseRx * RADIAL_FALLBACK_MAX_MULTIPLIER;
			let radiusRx = baseRx + RADIAL_FALLBACK_STEP_PX;
			let chosenAtRadius: PlacedNode | null = null;
			while (radiusRx <= maxRx && !chosenAtRadius) {
				const radiusRy = radiusRx * aspect;
				for (let step = 0; step < RADIAL_FALLBACK_ANGULAR_STEPS; step++) {
					const sign = step % 2 === 0 ? 1 : -1;
					const offset = Math.ceil(step / 2) * angleStep;
					const angle = naturalAngle + sign * offset;
					const candidate: PlacedNode = {
						id: neighbour.id,
						x: centerPos.x + Math.cos(angle) * radiusRx,
						y: centerPos.y + Math.sin(angle) * radiusRy,
					};

					let blocked = false;
					for (const ex of existing) {
						if (aabbOverlapFraction(candidate, size, ex, ex.size) > overlapThreshold) {
							blocked = true;
							break;
						}
					}
					if (blocked) continue;

					let collidesWithSibling = false;
					for (const sib of placed) {
						const sibSize = sizeOr(sizes, sib.id, FALLBACK_SIZE);
						if (aabbOverlapFraction(candidate, size, sib, sibSize) > overlapThreshold) {
							collidesWithSibling = true;
							break;
						}
					}
					if (collidesWithSibling) continue;

					chosenAtRadius = candidate;
					break;
				}
				radiusRx += RADIAL_FALLBACK_STEP_PX;
			}

			if (chosenAtRadius) {
				chosen = chosenAtRadius;
			} else {
				// Pathological case: even at the cap we couldn't find a
				// clear slot. Place the node at the cap radius along its
				// natural angle and accept the overlap — better than
				// disturbing the existing canvas.
				const cappedRy = maxRx * aspect;
				chosen = {
					id: neighbour.id,
					x: centerPos.x + Math.cos(naturalAngle) * maxRx,
					y: centerPos.y + Math.sin(naturalAngle) * cappedRy,
				};
			}
		}
		placed.push(chosen);
	}

	// "Soft" global collision resolution: every node — new AND existing —
	// is allowed to move so overlaps are resolved by sharing the
	// displacement instead of forcing the new node to absorb all of it.
	// This trades a small amount of "existing nodes drift slightly on
	// expansion" for cleaner final spacing. The caller writes ALL the
	// resolved positions back into `LayoutNodesAtom` (see `expandLayout`
	// returning `updated` in the delta), so this becomes the new
	// permanent rest position for every affected node — and the magnetic
	// spring smoothly carries each one from its old visual position to
	// the new rest.
	const allForResolve: PlacedNode[] = [
		...existing.map((e) => ({ id: e.id, x: e.x, y: e.y })),
		...placed,
	];
	const noneFixed = new Set<string>();
	resolveCollisions(
		allForResolve,
		sizes,
		noneFixed,
		COLLISION_PADDING_X,
		COLLISION_PADDING_Y,
		COLLISION_ITERATIONS,
	);

	// Mutate `existingPositions` (passed in by the caller) with the
	// updated coordinates so the caller can pick up the deltas.
	const allById = new Map(allForResolve.map((p) => [p.id, p]));
	for (const node of placed) {
		const updated = allById.get(node.id);
		if (updated) {
			node.x = updated.x;
			node.y = updated.y;
		}
	}
	for (const [id, pos] of existingPositions) {
		const updated = allById.get(id);
		if (updated) {
			pos.x = updated.x;
			pos.y = updated.y;
		}
	}

	return placed;
}
